import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import FlashContext from '~/App/Components/Flash/Context';
import useSyncBilling from '~/react-hooks/use-sync-billing';
import {
  PlandalfGateName,
  plandalfMode,
} from './gates';
import { usePlandalfSdk } from './use-plandalf-sdk';
import {
  PreparedGate,
  runVerifiedGate,
  VerifiedGateState,
} from './verified-gate';

export type PlandalfGateResult = { state: VerifiedGateState | 'legacy' };

const checkoutPreloadedSdks = new WeakSet<object>();

async function prepareGate(name: PlandalfGateName): Promise<PreparedGate> {
  const { data } = await axios.post<PreparedGate>(
    `/api/v3/billing/plandalf/gates/${name}`
  );
  return data;
}

export function usePlandalfGate() {
  const sdk = usePlandalfSdk();
  const { syncBilling } = useSyncBilling();
  const { createFlash } = useContext(FlashContext);
  const busy = useRef(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (
      !sdk ||
      typeof sdk.preload !== 'function' ||
      checkoutPreloadedSdks.has(sdk)
    ) {
      return;
    }

    checkoutPreloadedSdks.add(sdk);
    sdk.preload('checkout');
  }, [sdk]);

  async function trigger(
    name: PlandalfGateName,
    readAccess: () => boolean | Promise<boolean>
  ): Promise<PlandalfGateResult> {
    console.log('[Plandalf] Trigger gate:', name, { sdkReady: Boolean(sdk) });
    if (busy.current) return { state: 'dismissed' };
    busy.current = true;
    setChecking(true);

    try {
      if ((await readAccess()) === true) {
        return { state: 'allowed' };
      }

      if (!sdk) return { state: 'manual' };

      const prepared = await prepareGate(name);
      console.log('[Plandalf] Prepared gate:', prepared);
      if (
        sdk &&
        typeof sdk.gate !== 'function' &&
        typeof sdk.dispatch === 'function'
      ) {
        console.log('[Plandalf] Using legacy workflow dispatch');
        if (prepared.decision.state !== 'checkout') return { state: 'manual' };
        if (plandalfMode() !== 'live') return { state: 'manual' };
        const key = prepared.decision.offerSlug;
        console.log('[Plandalf] Legacy workflow key:', key);
        if (sdk.isWorkflowActive?.(key)) {
          sdk.dispatch(key);
          return { state: 'legacy' };
        }
        return { state: 'manual' };
      }

      const result = await runVerifiedGate({
        sdk,
        name,
        ...prepared,
        readAccess,
        syncBilling,
        mode: plandalfMode(),
      });
      console.log('[Plandalf] Gate result:', result);

      if (result.state === 'pending') {
        createFlash({
          id: 'plandalf-gate-pending',
          title: 'Confirming your upgrade',
          message: 'Refresh access before retrying. This does not charge again.',
        });
      }

      if (result.state === 'error') {
        createFlash({
          id: 'plandalf-gate-error',
          title: 'Checkout could not open',
          message: 'Try again or use Manage billing.',
          options: { style: 'danger' },
        });
      }

      return result;
    } catch (error) {
      createFlash({
        id: 'plandalf-gate-error',
        title: 'Could not check billing',
        message:
          error instanceof Error ? error.message : 'Could not check billing',
        options: { style: 'danger' },
      });
      return { state: 'error' };
    } finally {
      busy.current = false;
      setChecking(false);
    }
  }

  return { trigger, checking, sdk };
}
